import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { from, fromEvent, timer } from 'rxjs';
import { mergeMap, map, skipUntil, throttleTime } from 'rxjs/operators';
import { LOCATION, WINDOW } from '@mercadona/core/utils/tokens';
import * as i1 from '@mercadona/core/logger';
import { VERSION } from '@mercadona/common';

/**
 *
 * @param {MPlatformParamsService} mPlatformParams The platform params service
 * @param {MPlatformEventsService} mPlatformIntegration The platform events service
 * @returns {() => Promise<void>} A function returning a void promise
 */
function mInitializePlatformModuleFactory(mPlatformParams, mPlatformIntegration) {
    mPlatformParams.parseQueryParams();
    mPlatformIntegration.schedulePingEvent();
    return () => Promise.resolve();
}

const M_PORTAL_PARAM_PORTAL_TYPE = 'fwk_front_portal';
const M_PORTAL_PARAM_DEVICE_TYPE = 'fwk_front_device';
const M_PORTAL_PARAM_COMMUNICATION_TYPE = 'fwk_front_communication';
const M_PORTAL_PARAM_APP_ID = 'appId';
const M_PLATFORM_CONFIG = new InjectionToken('M_PLATFORM_CONFIG');
const M_SESSION_REFRESH_DELAY = new InjectionToken('M_SESSION_REFRESH_DELAY', {
    factory: () => 5000
});
const M_SESSION_REFRESH_THROTTLE = new InjectionToken('M_SESSION_REFRESH_THROTTLE', {
    factory: () => 4 * 60 * 1000 // 4 minutes
});
const M_SESSION_REFRESH_EVENTS = new InjectionToken('M_SESSION_REFRESH_EVENTS', {
    factory: () => ['click', 'keydown']
});

class MPlatformParamsService {
    constructor(logger, location) {
        this.logger = logger;
        this.location = location;
        this.params = new URLSearchParams();
    }
    parseQueryParams() {
        const params = new URLSearchParams(this.location.search);
        if (params.get(M_PORTAL_PARAM_PORTAL_TYPE)) {
            this.params = params;
            this.logger.debug('[MPlatform] Parsed query params from PEJEC', this.params.toString());
        }
    }
    get communicationType() {
        return this.params.get(M_PORTAL_PARAM_COMMUNICATION_TYPE);
    }
    get portalType() {
        return this.params.get(M_PORTAL_PARAM_PORTAL_TYPE) || 'standalone';
    }
    get deviceType() {
        return this.params.get(M_PORTAL_PARAM_DEVICE_TYPE) || 'unknown';
    }
    get isInsidePortals() {
        return this.portalType !== 'standalone';
    }
    /**
     * ID returned by PEJEC to identify the application on the portal
     *
     * @returns {string | null} The PEJEC app ID
     */
    get pejecAppId() {
        return this.params.get(M_PORTAL_PARAM_APP_ID);
    }
}
MPlatformParamsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformParamsService, deps: [{ token: i1.MLoggerService }, { token: LOCATION }], target: i0.ɵɵFactoryTarget.Injectable });
MPlatformParamsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformParamsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformParamsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.MLoggerService }, { type: Location, decorators: [{
                    type: Inject,
                    args: [LOCATION]
                }] }]; } });

class MPlatformEventsService {
    constructor(window, document, delay, throttle, events, mPlatformParamsService, mLoggerService) {
        this.window = window;
        this.document = document;
        this.delay = delay;
        this.throttle = throttle;
        this.events = events;
        this.mPlatformParamsService = mPlatformParamsService;
        this.mLoggerService = mLoggerService;
    }
    /**
     * Setups sending ping to portals when there is user activity.
     */
    schedulePingEvent() {
        if (!this.mPlatformParamsService.isInsidePortals) {
            return;
        }
        from(this.events)
            .pipe(mergeMap((ev) => fromEvent(this.document.body, ev).pipe(map(() => `document_${ev}`))), skipUntil(timer(this.delay)), throttleTime(this.throttle))
            .subscribe(() => this.sendPing());
    }
    openApp(url, appCode) {
        if (!this.mPlatformParamsService.isInsidePortals) {
            return;
        }
        const message = {
            appId: this.mPlatformParamsService.pejecAppId,
            eventId: 'closeApp',
            data: {
                codApp: appCode,
                appUrl: encodeURIComponent(url),
                fwkType: 'AF',
                navApp: true
            },
            toPejec: true
        };
        this.sendMessage(message);
    }
    openLegacyAppByTaskCode(taskCode, screenCode, fwkType = '2', dataToSend = {}) {
        if (!this.mPlatformParamsService.isInsidePortals) {
            return;
        }
        const message = {
            appId: this.mPlatformParamsService.pejecAppId,
            eventId: 'closeApp',
            data: {
                codTarea: taskCode,
                codPantalla: screenCode,
                contexto: encodeURIComponent(this.transformContext(dataToSend, fwkType)),
                fwkType,
                navApp: true
            },
            toPejec: true
        };
        this.sendMessage(message);
    }
    openLegacyApp(url, fwkType, dataToSend = {}) {
        if (!this.mPlatformParamsService.isInsidePortals) {
            return;
        }
        const message = {
            appId: this.mPlatformParamsService.pejecAppId,
            eventId: 'closeApp',
            data: {
                appUrl: encodeURIComponent(url),
                contexto: encodeURIComponent(this.transformContext(dataToSend, fwkType)),
                fwkType,
                navApp: true
            },
            toPejec: true
        };
        this.sendMessage(message);
    }
    /**
     * Format needed on Sencha app to get context because they don't have a router.
     *
     * @param {unknown} dataToSend Some data to encode
     * @param {'2' | '3'} fwkType The type of the framework
     * @returns {string} The context formatted
     */
    transformContext(dataToSend, fwkType) {
        const params = new URLSearchParams({
            input: btoa(encodeURIComponent(JSON.stringify(dataToSend))),
            // Required to work with Sencha but not really needed for Angular apps
            callbackId: Date.now().toString(),
            srcFwkType: fwkType
        });
        return params.toString();
    }
    closeApp() {
        if (!this.mPlatformParamsService.isInsidePortals) {
            return;
        }
        const message = {
            appId: this.mPlatformParamsService.pejecAppId,
            eventId: 'closeApp',
            toPejec: true
        };
        this.sendMessage(message);
    }
    sendPing() {
        this.sendMessage({
            appId: this.mPlatformParamsService.pejecAppId,
            eventId: 'doPing',
            toPejec: true
        });
    }
    sendMessage(message) {
        const communicationType = this.mPlatformParamsService.communicationType;
        if (communicationType === 'opener') {
            this.sendMessageToOpener(message);
        }
        else if (communicationType === 'parent') {
            this.sendMessageToParent(message);
        }
        else if (communicationType === 'mmessaging') {
            this.sendMessageToMessagingEndpoint(message);
        }
    }
    sendMessageToOpener(message) {
        const { eventId } = message;
        this.window.opener?.postMessage(JSON.stringify(message), '*');
        this.mLoggerService.debug(`[MPlatformService] Event "${eventId}" sent to OPENER`);
    }
    sendMessageToParent(message) {
        const { eventId } = message;
        this.window.parent.postMessage(JSON.stringify(message), '*');
        this.mLoggerService.debug(`[MPlatformService] Event "${eventId}" sent to PARENT`);
    }
    /**
     * We send a HTTP request, the PEM will intercept this request and process it.
     * The PEM app inject Cordova code to intercep this request and process it.
     * The App URL opened must contain "CORDOVA_VERSION" as query string to work.
     *
     * @param {PejecMessage} message The PEJEC message
     */
    sendMessageToMessagingEndpoint(message) {
        const { eventId } = message;
        const xhr = new XMLHttpRequest();
        let url = `${location.protocol}//${location.host}/mmessaging/${eventId}`;
        if (message.data) {
            const params = new URLSearchParams(message.data);
            url += `?${params.toString()}`.replace(/"/g, '&quot;');
        }
        xhr.open('GET', url, true);
        xhr.send();
        this.mLoggerService.debug(`[MPlatformService] Event "${eventId}" sent to PARENT`);
    }
}
MPlatformEventsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformEventsService, deps: [{ token: WINDOW }, { token: DOCUMENT }, { token: M_SESSION_REFRESH_DELAY }, { token: M_SESSION_REFRESH_THROTTLE }, { token: M_SESSION_REFRESH_EVENTS }, { token: MPlatformParamsService }, { token: i1.MLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
MPlatformEventsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformEventsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformEventsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [M_SESSION_REFRESH_DELAY]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [M_SESSION_REFRESH_THROTTLE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [M_SESSION_REFRESH_EVENTS]
                }] }, { type: MPlatformParamsService }, { type: i1.MLoggerService }]; } });

const isIpad = (win) => {
    // iOS 12 and below
    if (testUserAgent(win, /iPad/i)) {
        return true;
    }
    // iOS 13+
    if (testUserAgent(win, /Macintosh/i) && isMobile(win)) {
        return true;
    }
    return false;
};
const isIphone = (win) => testUserAgent(win, /iPhone/i);
const isIOS = (win) => testUserAgent(win, /iPhone/i) || isIpad(win);
const isWindows = (win) => testUserAgent(win, /Windows/i);
const isMac = (win) => testUserAgent(win, /Macintosh/i) && !isMobile(win);
const isLinux = (win) => testUserAgent(win, /Linux/i);
const isAndroid = (win) => testUserAgent(win, /android/i);
const isAndroidTablet = (win) => isAndroid(win) && !testUserAgent(win, /mobile/i);
const isTablet = (win) => isIpad(win) || isAndroidTablet(win);
const isMobile = (win) => matchMedia(win, '(any-pointer:coarse)');
const isPhone = (win) => isMobile(win) && !isTablet(win);
const isDesktop = (win) => !isMobile(win);
const isCordova = (win) => Boolean(win.cordova);
const isCapacitor = (win) => {
    const capacitor = win.Capacitor;
    return Boolean(capacitor && capacitor.isNative);
};
const isElectron = (win) => testUserAgent(win, /electron/i);
const isPWA = (win) => matchMedia(win, '(display-mode: standalone)');
const testUserAgent = (win, expr) => expr.test(win.navigator.userAgent);
const matchMedia = (win, query) => win.matchMedia(query).matches;
const getDevice = (win) => {
    // Device info is in the fisrt parenthesis of the user agent string
    const matches = /\(([^)]+)\)/.exec(win.navigator.userAgent);
    if (!matches) {
        return 'unknown';
    }
    // Get the last element of ";"" separated string, has the device model
    const deviceName = matches[1].split(';').pop();
    return deviceName ? deviceName.trim() : 'unknown';
};
const PLATFORMS_MAP = {
    windows: isWindows,
    linux: isLinux,
    mac: isMac,
    ios: isIOS,
    android: isAndroid,
    ipad: isIpad,
    iphone: isIphone,
    phone: isPhone,
    tablet: isTablet,
    mobile: isMobile,
    desktop: isDesktop,
    cordova: isCordova,
    capacitor: isCapacitor,
    electron: isElectron,
    pwa: isPWA
};
const DEVICE_INFO = {
    os: [
        {
            name: 'ios',
            is: isIOS
        },
        {
            name: 'android',
            is: isAndroid
        },
        {
            name: 'windows',
            is: isWindows
        },
        {
            name: 'linux',
            is: isLinux
        },
        {
            name: 'mac',
            is: isMac
        },
        {
            name: 'unknown',
            is: () => true
        }
    ],
    deviceType: [
        {
            name: 'phone',
            is: isPhone
        },
        {
            name: 'tablet',
            is: isTablet
        },
        {
            name: 'desktop',
            is: isDesktop
        },
        {
            name: 'unknown',
            is: () => true
        }
    ],
    runtime: [
        {
            name: 'cordova',
            is: isCordova
        },
        {
            name: 'capacitor',
            is: isCapacitor
        },
        {
            name: 'electron',
            is: isElectron
        },
        {
            name: 'pwa',
            is: isPWA
        },
        {
            name: 'browser',
            is: () => true
        }
    ]
};

class MPlatformService {
    constructor(mPlatfomrParams, mPlatformEvents, window, config) {
        this.mPlatfomrParams = mPlatfomrParams;
        this.mPlatformEvents = mPlatformEvents;
        this.window = window;
        this.config = config;
    }
    /**
     * Return the type of communication with the portal.
     *
     * @returns {MPlatformCommunicationType} The type of platform communication
     */
    get communicationType() {
        return this.mPlatfomrParams.communicationType;
    }
    /**
     * The portal from where the application is opened.
     *
     * @returns {MPlatformPortalType} The type of platform portal
     */
    get portalType() {
        return this.mPlatfomrParams.portalType;
    }
    /**
     * Type of device with which the application is opened in the portal.
     *
     * Only types of devices recognized by Mercadona.
     *
     * @returns {MPlatformDeviceType} The type of platform device
     */
    get deviceType() {
        return this.mPlatfomrParams.deviceType;
    }
    /**
     * Short name of the application.
     *
     * @returns {string} The name of the application
     */
    get appName() {
        return this.config.appName;
    }
    /**
     * Return the version of the app.
     *
     * @returns {string | undefined} The version of the application
     */
    get appVersion() {
        return this.config.appVersion;
    }
    /**
     * Return in which environment is running the application.
     *
     * @returns {MPlatformEnvironment | undefined} The environment of the platform
     */
    get environment() {
        return this.config.environment;
    }
    /**
     * Return the version of the framework.
     *
     * @returns {string} The framework version
     */
    get fwkVersion() {
        return VERSION;
    }
    /**
     * Check if the app is opened from any portal.
     *
     * @returns {boolean} Returns if the app is inside portals
     */
    get isInsidePortals() {
        return this.mPlatfomrParams.isInsidePortals;
    }
    /**
     * Return if the app is running in the platform.
     * Use User-Agent to detect the platform.
     *
     * @param {MPlatform} platform A platform type.
     * @returns {boolean} Returns if the app is running in the platform.
     */
    is(platform) {
        return PLATFORMS_MAP[platform](this.window);
    }
    /**
     * Return the device information based on the User-Agent.
     *
     * @returns {MDeviceInfo} with all the information about the device.
     */
    getDeviceInfo() {
        return {
            os: DEVICE_INFO.os.find((os) => os.is(this.window))?.name,
            deviceType: DEVICE_INFO.deviceType.find((device) => device.is(this.window))?.name,
            runtime: DEVICE_INFO.runtime.find((runtime) => runtime.is(this.window))?.name,
            device: getDevice(this.window)
        };
    }
    /**
     * Open Angular app by URL on PEJEC.
     *
     * @param {string} url URL to open
     * @param {string} appCode App Code ID from PEJEC
     */
    openApp(url, appCode) {
        this.mPlatformEvents.openApp(url, appCode);
    }
    /**
     * Open legacy app (Sencha) on PEJEC
     *
     * @param {string} url URL of the app
     * @param {'2' | '3'} fwkType Framework version of the app: FWK2 or FWK3
     * @param {unknown} dataToSend Optional. Object params to sent to the app
     */
    openLegacyApp(url, fwkType, dataToSend) {
        this.mPlatformEvents.openLegacyApp(url, fwkType, dataToSend);
    }
    /**
     * Open legacy app (Sencha) on PEJEC with task code and screen code
     *
     * @param {string} taskCode Task Code provided by PEJEC
     * @param {string} screenCode Screen Code provided by PEJEC
     * @param {'2' | '3'} fwkType Framework version of the app: FWK2 or FWK3
     * @param {unknown} dataToSend Optional. Object params to sent to the app
     */
    openLegacyAppByTaskCode(taskCode, screenCode, fwkType, dataToSend) {
        this.mPlatformEvents.openLegacyAppByTaskCode(taskCode, screenCode, fwkType, dataToSend);
    }
    /**
     * Send close event to PEJEC MOV.
     * Only works on PEM
     *
     */
    closeApp() {
        this.mPlatformEvents.closeApp();
    }
}
MPlatformService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformService, deps: [{ token: MPlatformParamsService }, { token: MPlatformEventsService }, { token: WINDOW }, { token: M_PLATFORM_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
MPlatformService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: MPlatformParamsService }, { type: MPlatformEventsService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [M_PLATFORM_CONFIG]
                }] }]; } });

class MPlatformModule {
    static forRoot(config) {
        return {
            ngModule: MPlatformModule,
            providers: [
                MPlatformService,
                MPlatformParamsService,
                MPlatformEventsService,
                {
                    provide: M_PLATFORM_CONFIG,
                    useValue: config
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: mInitializePlatformModuleFactory,
                    deps: [MPlatformParamsService, MPlatformEventsService],
                    multi: true
                }
            ]
        };
    }
}
MPlatformModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MPlatformModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MPlatformModule });
MPlatformModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MPlatformModule, decorators: [{
            type: NgModule
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MPlatformModule, MPlatformService };
